import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Button,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  ExpandMore,
  Business,
  Person,
  AttachMoney,
  DateRange,
  Home,
  Phone,
  Email,
  Badge,
  Description,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { applicationAPI } from '../api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const MyApplication = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingApp, setLoadingApp] = useState(null);

  // Build URL with SSO token for external apps (same flow as sidebar)
  const buildRedirectUrl = async (baseUrl, appType = 'application') => {
    const url = new URL(baseUrl);
    const coreloanhubUrl = import.meta.env.VITE_CORELOANHUB_URL || 'https://coreloanhub.com';
    const returnParam = appType === 'pfs' ? 'pfs_submitted=true' : 'submitted=true';
    const returnUrl = `${coreloanhubUrl}/dashboard?${returnParam}&source=${appType === 'pfs' ? 'pfs-copilot' : 'financing-app'}`;
    url.searchParams.append('returnUrl', returnUrl);
    if (user?.email) {
      url.searchParams.append('email', user.email);
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/auth/generate-single-use-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      if (response.data.success) {
        url.searchParams.append('authToken', response.data.token);
      }
    } catch (error) {
      console.error('Failed to generate auth token:', error);
    }
    return url.toString();
  };

  const handleCardClick = async (appType) => {
    const urlMap = {
      application: import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com',
      pfs: import.meta.env.VITE_PFS_URL || 'https://pfs.corebusinesscapital.com',
    };
    const baseUrl = urlMap[appType];
    setLoadingApp(appType);
    try {
      const redirectUrl = await buildRedirectUrl(baseUrl, appType);
      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening app:', error);
      window.open(baseUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setLoadingApp(null);
    }
  };

  useEffect(() => {
    fetchApplication();
  }, []);

  const fetchApplication = async () => {
    try {
      setLoading(true);
      const response = await applicationAPI.getAll();
      // Get the most recent application
      const apps = response.data?.data || [];
      if (apps.length > 0) {
        setApplication(apps[0]); // Most recent first (sorted by createdAt desc)
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching application:', err);
      setError(t('myApplication.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  // Determine if application is complete based on required fields
  const isApplicationComplete = (app) => {
    if (!app) return false;
    
    const requiredFields = [
      'fundingAmount',
      'fundingPurpose',
      'timeFrame',
      'businessLegalName',
      'businessEntityType',
      'productsServices',
      'employeeCount',
      'ein',
      'businessAddress',
      'businessStartDate',
      'monthlyGrossSales',
      'ownOrRentBusiness',
    ];

    const ownerRequiredFields = [
      'ownershipPercentage',
      'firstName',
      'lastName',
      'dob',
      'mobilePhone',
      'email',
      'homeAddress',
      'ownHome',
    ];

    // Check main fields
    const mainFieldsComplete = requiredFields.every(field => {
      const value = app[field];
      return value !== undefined && value !== null && value !== '';
    });

    // Check owner1 fields
    const owner1Complete = app.owner1 && ownerRequiredFields.every(field => {
      const value = app.owner1[field];
      return value !== undefined && value !== null && value !== '';
    });

    return mainFieldsComplete && owner1Complete;
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return t('myApplication.notProvided');
    return new Intl.NumberFormat(i18n.language === 'es' ? 'es-US' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('myApplication.notProvided');
    return new Date(dateStr).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTimeFrame = (timeFrame) => {
    const labels = {
      oneToTwoDays: t('myApplication.tf_oneToTwoDays'),
      twoToThreeDays: t('myApplication.tf_twoToThreeDays'),
      threeToFiveDays: t('myApplication.tf_threeToFiveDays'),
      fiveToTenDays: t('myApplication.tf_fiveToTenDays'),
      twoToThreeWeeks: t('myApplication.tf_twoToThreeWeeks'),
      endOfThisMonth: t('myApplication.tf_endOfThisMonth'),
      endOfThisQuarter: t('myApplication.tf_endOfThisQuarter'),
      endOfThisYear: t('myApplication.tf_endOfThisYear'),
      beginningOfNextYear: t('myApplication.tf_beginningOfNextYear'),
    };
    return labels[timeFrame] || timeFrame || t('myApplication.notProvided');
  };

  const formatEntityType = (type) => {
    const labels = {
      corporation: t('myApplication.entityCorporation'),
      partnership: t('myApplication.entityPartnership'),
      llc: t('myApplication.entityLlc'),
      soleProprietorship: t('myApplication.entitySoleProprietorship'),
    };
    return labels[type] || type || t('myApplication.notProvided');
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      draft: { label: t('myApplication.statusDraft'), color: 'default', icon: <Description /> },
      submitted: { label: t('myApplication.statusSubmitted'), color: 'primary', icon: <CheckCircle /> },
      under_review: { label: t('myApplication.statusUnderReview'), color: 'info', icon: <Description /> },
      approved: { label: t('myApplication.statusApproved'), color: 'success', icon: <CheckCircle /> },
      rejected: { label: t('myApplication.statusRejected'), color: 'error', icon: <Warning /> },
      more_info_needed: { label: t('myApplication.statusMoreInfo'), color: 'warning', icon: <Warning /> },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="medium"
        sx={{ fontWeight: 600, px: 1 }}
      />
    );
  };

  const InfoItem = ({ icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
      <Box sx={{ color: 'primary.main', mt: 0.3 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {value || t('myApplication.notProvided')}
        </Typography>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={48} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      </Container>
    );
  }

  if (!application) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Box sx={{ fontSize: '4rem', mb: 2 }}>📋</Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {t('myApplication.noApplicationFound')}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t('myApplication.noApplicationDesc')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={async () => {
              const baseUrl = import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com';
              try {
                const redirectUrl = await buildRedirectUrl(baseUrl, 'application');
                window.open(redirectUrl, '_blank', 'noopener,noreferrer');
              } catch (err) {
                window.open(baseUrl, '_blank', 'noopener,noreferrer');
              }
            }}
            sx={{ fontWeight: 600, px: 4, py: 1.5 }}
          >
            {t('myApplication.startApplication') || 'Start Application'}
          </Button>
        </Paper>
      </Container>
    );
  }

  const isComplete = isApplicationComplete(application);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Welcome Header */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.2),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#0d2137' }}>
              {t('myApplication.welcomeBack', { name: user?.firstName || application.owner1?.firstName || 'Valued Applicant' })}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('myApplication.applicationSummary')}{' '}
              <strong>{formatDate(application.submittedAt || application.createdAt)}</strong>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">{t('myApplication.applicationStatus')}</Typography>
              {getStatusChip(application.status)}
            </Box>
            <Chip
              icon={isComplete ? <CheckCircle /> : <Warning />}
              label={isComplete ? t('myApplication.complete') : t('myApplication.incomplete')}
              color={isComplete ? 'success' : 'warning'}
              variant="outlined"
              size="medium"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Application Summary */}
      <Grid container spacing={3}>
        {/* Completion Tracking Section - Moved to top for visibility */}
        {application.completionTracking && (
          <Grid item xs={12}>
            <Accordion defaultExpanded elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', borderRadius: '8px 8px 0 0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircle sx={{ color: 'success.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('myApplication.completionStatus')}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Tooltip
                      title={!application.completionTracking.generalApplicationComplete ? t('myApplication.clickToCompleteApp') || 'Click to complete General Application' : ''}
                      arrow
                    >
                      <Paper
                        variant="outlined"
                        onClick={!application.completionTracking.generalApplicationComplete ? () => handleCardClick('application') : undefined}
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          borderColor: application.completionTracking.generalApplicationComplete ? 'success.main' : 'grey.300',
                          bgcolor: application.completionTracking.generalApplicationComplete ? alpha(theme.palette.success.main, 0.05) : 'grey.50',
                          ...(!application.completionTracking.generalApplicationComplete && {
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              borderColor: 'warning.main',
                              bgcolor: alpha(theme.palette.warning.main, 0.05),
                              transform: 'translateY(-2px)',
                              boxShadow: 2,
                            },
                          }),
                        }}
                      >
                        {loadingApp === 'application' ? (
                          <CircularProgress size={40} sx={{ mb: 1 }} />
                        ) : application.completionTracking.generalApplicationComplete ? (
                          <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                        ) : (
                          <Warning sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                        )}
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('myApplication.generalApplication')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {application.completionTracking.generalApplicationComplete ? t('myApplication.completed') : t('myApplication.incomplete')}
                        </Typography>
                        {application.completionTracking.generalApplicationDate && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {t('myApplication.submitted')} {formatDate(application.completionTracking.generalApplicationDate)}
                          </Typography>
                        )}
                        {!application.completionTracking.generalApplicationComplete && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1.5 }}>
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                              {t('myApplication.completeNow') || 'Complete Now'}
                            </Typography>
                            <OpenInNewIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                          </Box>
                        )}
                      </Paper>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Tooltip
                      title={!application.completionTracking.sbaForm413Complete ? t('myApplication.clickToCompletePfs') || 'Click to complete SBA Form 413' : ''}
                      arrow
                    >
                      <Paper
                        variant="outlined"
                        onClick={!application.completionTracking.sbaForm413Complete ? () => handleCardClick('pfs') : undefined}
                        sx={{
                          p: 3,
                          textAlign: 'center',
                          borderColor: application.completionTracking.sbaForm413Complete ? 'success.main' : 'grey.300',
                          bgcolor: application.completionTracking.sbaForm413Complete ? alpha(theme.palette.success.main, 0.05) : 'grey.50',
                          ...(!application.completionTracking.sbaForm413Complete && {
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              borderColor: 'warning.main',
                              bgcolor: alpha(theme.palette.warning.main, 0.05),
                              transform: 'translateY(-2px)',
                              boxShadow: 2,
                            },
                          }),
                        }}
                      >
                        {loadingApp === 'pfs' ? (
                          <CircularProgress size={40} sx={{ mb: 1 }} />
                        ) : application.completionTracking.sbaForm413Complete ? (
                          <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                        ) : (
                          <Warning sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                        )}
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('myApplication.sbaForm413')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {application.completionTracking.sbaForm413Complete ? t('myApplication.completed') : t('myApplication.incomplete')}
                        </Typography>
                        {application.completionTracking.sbaForm413Date && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {t('myApplication.submitted')} {formatDate(application.completionTracking.sbaForm413Date)}
                          </Typography>
                        )}
                        {!application.completionTracking.sbaForm413Complete && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1.5 }}>
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                              {t('myApplication.completeNow') || 'Complete Now'}
                            </Typography>
                            <OpenInNewIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                          </Box>
                        )}
                      </Paper>
                    </Tooltip>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}

        {/* Data Verification Note - Moved to top for visibility */}
        <Grid item xs={12}>
          <Alert 
            severity="info" 
            icon={<CheckCircle />}
            sx={{ 
              borderRadius: 2,
              '& .MuiAlert-message': { width: '100%' }
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('myApplication.dataVerified')}
            </Typography>
            <Typography variant="body2">
              {t('myApplication.dataVerifiedDesc')}
            </Typography>
          </Alert>
        </Grid>

        {/* Funding Details Section */}
        <Grid item xs={12}>
          <Accordion defaultExpanded elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', borderRadius: '8px 8px 0 0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <AttachMoney sx={{ color: 'success.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('myApplication.fundingDetails')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <InfoItem
                    icon={<AttachMoney fontSize="small" />}
                    label={t('myApplication.fundingAmountRequested')}
                    value={formatCurrency(application.fundingAmount)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem
                    icon={<Description fontSize="small" />}
                    label={t('myApplication.fundingPurpose')}
                    value={application.fundingPurpose}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem
                    icon={<DateRange fontSize="small" />}
                    label={t('myApplication.timeFrameNeeded')}
                    value={formatTimeFrame(application.timeFrame)}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Business Details Section */}
        <Grid item xs={12}>
          <Accordion defaultExpanded elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', borderRadius: '8px 8px 0 0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Business sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('myApplication.businessInformation')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InfoItem
                    icon={<Business fontSize="small" />}
                    label={t('myApplication.legalBusinessName')}
                    value={application.businessLegalName}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem
                    icon={<Badge fontSize="small" />}
                    label={t('myApplication.entityType')}
                    value={formatEntityType(application.businessEntityType)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InfoItem
                    icon={<Description fontSize="small" />}
                    label={t('myApplication.productsServices')}
                    value={application.productsServices}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem
                    icon={<Person fontSize="small" />}
                    label={t('myApplication.numberOfEmployees')}
                    value={application.employeeCount}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem
                    icon={<Badge fontSize="small" />}
                    label={t('myApplication.einTaxId')}
                    value={application.ein ? `***-**-${application.ein.slice(-4)}` : t('myApplication.notProvided')}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem
                    icon={<DateRange fontSize="small" />}
                    label={t('myApplication.businessStartDate')}
                    value={formatDate(application.businessStartDate)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InfoItem
                    icon={<Home fontSize="small" />}
                    label={t('myApplication.businessAddress')}
                    value={application.businessAddress}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem
                    icon={<AttachMoney fontSize="small" />}
                    label={t('myApplication.monthlyGrossSales')}
                    value={formatCurrency(application.monthlyGrossSales)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem
                    icon={<Home fontSize="small" />}
                    label={t('myApplication.ownOrRentBusinessLocation')}
                    value={application.ownOrRentBusiness === 'own' ? t('myApplication.own') : t('myApplication.rent')}
                  />
                </Grid>
                {application.monthlyBusinessPayment > 0 && (
                  <Grid item xs={12} md={4}>
                    <InfoItem
                      icon={<AttachMoney fontSize="small" />}
                      label={t('myApplication.monthlyBusinessPayment')}
                      value={formatCurrency(application.monthlyBusinessPayment)}
                    />
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                {t('myApplication.additionalBusinessInfo')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderColor: application.hasOutstandingDebt ? 'warning.main' : 'success.main',
                      bgcolor: application.hasOutstandingDebt ? alpha(theme.palette.warning.main, 0.05) : alpha(theme.palette.success.main, 0.05),
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">{t('myApplication.outstandingDebt')}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {application.hasOutstandingDebt ? t('common.yes') : t('common.no')}
                    </Typography>
                    {application.hasOutstandingDebt && application.outstandingBalance && (
                      <Typography variant="caption" color="text.secondary">
                        {t('myApplication.balance')} {formatCurrency(application.outstandingBalance)}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderColor: application.hasPendingLitigation ? 'error.main' : 'success.main',
                      bgcolor: application.hasPendingLitigation ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.success.main, 0.05),
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">{t('myApplication.pendingLitigation')}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {application.hasPendingLitigation ? t('common.yes') : t('common.no')}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderColor: application.hasOpenJudgments ? 'error.main' : 'success.main',
                      bgcolor: application.hasOpenJudgments ? alpha(theme.palette.error.main, 0.05) : alpha(theme.palette.success.main, 0.05),
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">{t('myApplication.openJudgments')}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {application.hasOpenJudgments ? t('common.yes') : t('common.no')}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderColor: 'grey.300',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">{t('myApplication.franchise')}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {application.isFranchise ? t('common.yes') : t('common.no')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Owner 1 Details Section */}
        <Grid item xs={12} md={application.hasAdditionalOwner ? 6 : 12}>
          <Accordion defaultExpanded elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', '&:before': { display: 'none' }, height: '100%' }}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', borderRadius: '8px 8px 0 0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Person sx={{ color: 'info.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('myApplication.primaryOwner')}</Typography>
                {application.owner1?.ownershipPercentage && (
                  <Chip
                    label={t('myApplication.ownership', { percent: application.owner1.ownershipPercentage })}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {application.owner1 ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <InfoItem
                      icon={<Person fontSize="small" />}
                      label={t('myApplication.fullName')}
                      value={`${application.owner1.firstName || ''} ${application.owner1.lastName || ''}`.trim() || t('myApplication.notProvided')}
                    />
                  </Grid>
                  {application.owner1.professionalTitle && (
                    <Grid item xs={12} md={6}>
                      <InfoItem
                        icon={<Badge fontSize="small" />}
                        label={t('myApplication.title')}
                        value={application.owner1.professionalTitle}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <InfoItem
                      icon={<DateRange fontSize="small" />}
                      label={t('myApplication.dateOfBirth')}
                      value={formatDate(application.owner1.dob)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem
                      icon={<Phone fontSize="small" />}
                      label={t('myApplication.mobilePhone')}
                      value={application.owner1.mobilePhone}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem
                      icon={<Email fontSize="small" />}
                      label={t('myApplication.email')}
                      value={application.owner1.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InfoItem
                      icon={<Home fontSize="small" />}
                      label={t('myApplication.homeAddress')}
                      value={application.owner1.homeAddress}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem
                      icon={<Home fontSize="small" />}
                      label={t('myApplication.ownOrRentHome')}
                      value={application.owner1.ownHome === 'own' ? t('myApplication.own') : t('myApplication.rent')}
                    />
                  </Grid>
                  {application.owner1.monthlyHomePayment > 0 && (
                    <Grid item xs={12} md={6}>
                      <InfoItem
                        icon={<AttachMoney fontSize="small" />}
                        label={t('myApplication.monthlyHomePayment')}
                        value={formatCurrency(application.owner1.monthlyHomePayment)}
                      />
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography color="text.secondary">{t('myApplication.ownerInfoNotProvided')}</Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Owner 2 Details Section (if exists) */}
        {application.hasAdditionalOwner && application.owner2 && (
          <Grid item xs={12} md={6}>
            <Accordion defaultExpanded elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', '&:before': { display: 'none' }, height: '100%' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', borderRadius: '8px 8px 0 0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Person sx={{ color: 'secondary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('myApplication.additionalOwner')}</Typography>
                  {application.owner2?.ownershipPercentage && (
                    <Chip
                      label={t('myApplication.ownership', { percent: application.owner2.ownershipPercentage })}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <InfoItem
                      icon={<Person fontSize="small" />}
                      label={t('myApplication.fullName')}
                      value={`${application.owner2.firstName || ''} ${application.owner2.lastName || ''}`.trim() || t('myApplication.notProvided')}
                    />
                  </Grid>
                  {application.owner2.professionalTitle && (
                    <Grid item xs={12} md={6}>
                      <InfoItem
                        icon={<Badge fontSize="small" />}
                        label={t('myApplication.title')}
                        value={application.owner2.professionalTitle}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <InfoItem
                      icon={<DateRange fontSize="small" />}
                      label={t('myApplication.dateOfBirth')}
                      value={formatDate(application.owner2.dob)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem
                      icon={<Phone fontSize="small" />}
                      label={t('myApplication.mobilePhone')}
                      value={application.owner2.mobilePhone}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem
                      icon={<Email fontSize="small" />}
                      label={t('myApplication.email')}
                      value={application.owner2.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InfoItem
                      icon={<Home fontSize="small" />}
                      label={t('myApplication.homeAddress')}
                      value={application.owner2.homeAddress}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoItem
                      icon={<Home fontSize="small" />}
                      label={t('myApplication.ownOrRentHome')}
                      value={application.owner2.ownHome === 'own' ? t('myApplication.own') : t('myApplication.rent')}
                    />
                  </Grid>
                  {application.owner2.monthlyHomePayment > 0 && (
                    <Grid item xs={12} md={6}>
                      <InfoItem
                        icon={<AttachMoney fontSize="small" />}
                        label={t('myApplication.monthlyHomePayment')}
                        value={formatCurrency(application.owner2.monthlyHomePayment)}
                      />
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        )}

      </Grid>
    </Container>
  );
};

export default MyApplication;
